import { Vector2 } from 'three';
import { GEOMETRY_EPSILON as EPS } from '@/helpers/angleConstants';

function ensureCCW(polygon: { x: number; y: number }[]) {
  const signedArea = polygon.reduce((sum, curr, i, arr) => {
    const next = arr[(i + 1) % arr.length];
    return sum + (curr.x * next.y - next.x * curr.y);
  }, 0);
  return signedArea < 0 ? [...polygon].reverse() : polygon;
}

export const makeOffsetPoly = (pointsRaw: Vector2[], offset: number) => {
  const outer_ccw = 1;
  const points = ensureCCW(pointsRaw);
  const output: Vector2[] = [];
  const num_points = points.length - 1;

  for (let curr = 0; curr < num_points; curr++) {
    const prev = (curr + num_points - 1) % num_points;
    const next = (curr + 1) % num_points;

    const vn = new Vector2(points[next].x - points[curr].x, points[next].y - points[curr].y);
    const vnn = vn.normalize();
    const nnnX = vnn.y;
    const nnnY = -vnn.x;

    const vp = new Vector2(points[curr].x - points[prev].x, points[curr].y - points[prev].y);
    const vpn = vp.normalize();
    const npnX = vpn.y * outer_ccw;
    const npnY = -vpn.x * outer_ccw;

    const bis = new Vector2((nnnX + npnX) * outer_ccw, (nnnY + npnY) * outer_ccw);
    const bisn = bis.normalize();
    const bislen = offset / Math.sqrt((1 + nnnX * npnX + nnnY * npnY) / 2);

    output.push(new Vector2(points[curr].x + bislen * bisn.x, points[curr].y + bislen * bisn.y));
  }

  output.push(output[0]);
  return output;
};

function doPolygonsIntersect(polygonA: Vector2[], polygonB: Vector2[]) {
  const polygons = [polygonA, polygonB];
  let minA;
  let maxA;
  let projected;
  let i;
  let i1;
  let j;
  let minB;
  let maxB;

  for (i = 0; i < polygons.length; i++) {
    // for each polygon, look at each edge of the polygon, and determine if it separates
    // the two shapes
    const polygon = polygons[i];
    for (i1 = 0; i1 < polygon.length; i1++) {
      // grab 2 vertices to create an edge
      const i2 = (i1 + 1) % polygon.length;
      const p1 = polygon[i1];
      const p2 = polygon[i2];

      // find the line perpendicular to this edge
      const normal = { x: p2.y - p1.y, y: p1.x - p2.x };

      minA = undefined;
      maxA = undefined;
      // for each vertex in the first shape, project it onto the line perpendicular to the edge
      // and keep track of the min and max of these values
      for (j = 0; j < polygonA.length; j++) {
        projected = normal.x * polygonA[j].x + normal.y * polygonA[j].y;
        if (minA === undefined || projected < minA) {
          minA = projected;
        }
        if (maxA === undefined || projected > maxA) {
          maxA = projected;
        }
      }

      // for each vertex in the second shape, project it onto the line perpendicular to the edge
      // and keep track of the min and max of these values
      minB = undefined;
      maxB = undefined;
      for (j = 0; j < polygonB.length; j++) {
        projected = normal.x * polygonB[j].x + normal.y * polygonB[j].y;
        if (minB === undefined || projected < minB) {
          minB = projected;
        }
        if (maxB === undefined || projected > maxB) {
          maxB = projected;
        }
      }

      // if there is no overlap between the projects, the edge we are looking at separates the two
      // polygons, and we know there is no overlap
      if (maxA < minB || maxB < minA) {
        return false;
      }
    }
  }

  return true;
}

export const polygonCentroid = (points: { x: number; y: number }[]): Vector2 => {
  let x = 0;
  let y = 0;
  let area = 0;

  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    const cross = p1.x * p2.y - p2.x * p1.y;
    x += (p1.x + p2.x) * cross;
    y += (p1.y + p2.y) * cross;
    area += cross;
  }

  area *= 0.5;
  return new Vector2(x / (6 * area), y / (6 * area));
};

/**
 * Robust line intersection for lines (A-B) and (C-D)
 * Returns intersection point or `current` when lines are (nearly) parallel.
 */
function sutherlandHodgmanIntersection<P extends { x: number; y: number }>(
  prev: P,
  current: P,
  clipStart: P,
  clipEnd: P,
): { x: number; y: number } {
  const A = clipStart;
  const B = clipEnd;
  const C = prev;
  const D = current;

  const denom = (A.x - B.x) * (C.y - D.y) - (A.y - B.y) * (C.x - D.x);

  if (Math.abs(denom) < EPS) {
    // Nearly parallel/coincident — return current as a fallback
    return { x: current.x, y: current.y };
  }

  const n1 = A.x * B.y - A.y * B.x; // A x B (scalar)
  const n2 = C.x * D.y - C.y * D.x; // C x D (scalar)

  const x = (n1 * (C.x - D.x) - (A.x - B.x) * n2) / denom;
  const y = (n1 * (C.y - D.y) - (A.y - B.y) * n2) / denom;

  return { x, y };
}

function sutherlandHodgmanIsPInside<P extends { x: number; y: number }>(
  p: P,
  clipStart: P,
  clipEnd: P,
): boolean {
  // inside = left of edge (clipStart -> clipEnd) for CCW clip polygon
  return (
    (clipEnd.x - clipStart.x) * (p.y - clipStart.y) -
      (clipEnd.y - clipStart.y) * (p.x - clipStart.x) >=
    -EPS // small tolerance so points on boundary count as inside
  );
}

function sutherlandHodgman(
  subjectRaw: { x: number; y: number }[],
  clipRaw: { x: number; y: number }[],
): { x: number; y: number }[] {
  // guards
  if (subjectRaw.length < 3 || clipRaw.length < 3) return [];

  const subject = ensureCCW(subjectRaw);
  const clip = ensureCCW(clipRaw);

  let output = [...subject];

  for (let i = 0; i < clip.length; i++) {
    const clipStart = clip[i];
    const clipEnd = clip[(i + 1) % clip.length];
    const input = output;
    output = [];

    if (input.length === 0) break; // early exit — nothing left

    for (let j = 0; j < input.length; j++) {
      const current = input[j];
      const prev = input[(j - 1 + input.length) % input.length];

      const currInside = sutherlandHodgmanIsPInside(current, clipStart, clipEnd);
      const prevInside = sutherlandHodgmanIsPInside(prev, clipStart, clipEnd);

      if (currInside) {
        if (!prevInside) {
          output.push(sutherlandHodgmanIntersection(prev, current, clipStart, clipEnd));
        }
        output.push(current);
      } else if (prevInside) {
        output.push(sutherlandHodgmanIntersection(prev, current, clipStart, clipEnd));
      }
    }
  }

  return output;
}

export function findPolygonOverlap(
  polyA: { x: number; y: number }[],
  polyB: { x: number; y: number }[],
):
  | {
      x: number;
      y: number;
    }[]
  | undefined {
  const overlapPoly = sutherlandHodgman(polyA, polyB);

  if (overlapPoly.length < 3) return undefined;

  return overlapPoly;
}

/**
 * @description converts a polyline to a polygon by adding the first point to the end of polyline again
 */
export const polylineToPolygon = (polyline: Vector2[]): Vector2[] => [
  ...polyline,
  polyline[0].clone(),
];
